import { useState, useMemo, useCallback } from 'react';

interface EquipmentFilters {
  equipmentNumber: string[];
  equipmentName: string[];
  equipmentType: string[];
  serialNumber: string[];
}

interface UseEquipmentFiltersProps {
  equipmentList: any[];
}

export const useEquipmentFilters = ({ equipmentList }: UseEquipmentFiltersProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<EquipmentFilters>({
    equipmentNumber: [],
    equipmentName: [],
    equipmentType: [],
    serialNumber: [],
  });

  // Extract unique equipment numbers from equipment list
  const uniqueEquipmentNumbers = useMemo(() => {
    const equipmentNumbers = new Set<string>();
    equipmentList.forEach((equipment: any) => {
      const eqNumber = equipment.equipmentNumber || equipment.equipment_number;
      if (eqNumber) {
        equipmentNumbers.add(String(eqNumber));
      }
    });
    return Array.from(equipmentNumbers).sort();
  }, [equipmentList]);

  // Extract unique equipment names from equipment list
  const uniqueEquipmentNames = useMemo(() => {
    const equipmentNames = new Set<string>();
    equipmentList.forEach((equipment: any) => {
      const eqName = equipment.equipmentName || equipment.equipment_name;
      if (eqName) {
        equipmentNames.add(String(eqName));
      }
    });
    return Array.from(equipmentNames).sort();
  }, [equipmentList]);

  // Extract unique equipment types from equipment list
  const uniqueEquipmentTypes = useMemo(() => {
    const equipmentTypes = new Set<string>();
    equipmentList.forEach((equipment: any) => {
      const eqType = equipment.equipmentName || equipment.equipment_name;
      if (eqType) {
        equipmentTypes.add(String(eqType));
      }
    });
    return Array.from(equipmentTypes).sort();
  }, [equipmentList]);

  // Extract unique serial numbers from equipment list
  const uniqueSerialNumbers = useMemo(() => {
    const serialNumbers = new Set<string>();
    equipmentList.forEach((equipment: any) => {
      const serialNum = equipment.serial_number;
      if (serialNum) {
        serialNumbers.add(String(serialNum));
      }
    });
    return Array.from(serialNumbers).sort();
  }, [equipmentList]);

  // Filter equipment based on search query and filters
  const filteredEquipment = useMemo(() => {
    return equipmentList.filter((equipment: any) => {
      // Search query filter - search across all fields
      const searchLower = searchQuery.toLowerCase();
      const equipmentName = (equipment.equipmentName || equipment.equipment_name || '').toLowerCase();
      const equipmentNumber = (equipment.equipmentNumber || equipment.equipment_number || '').toLowerCase();
      const notes = (equipment?.extra_details?.Notes || equipment?.notes || '').toLowerCase();
      
      const matchesSearch = !searchQuery || 
        equipmentName.includes(searchLower) ||
        equipmentNumber.includes(searchLower) ||
        notes.includes(searchLower) ||
        (equipment.id?.toString() || '').includes(searchLower);

      // Equipment number filter
      const matchesEquipmentNumber = filters.equipmentNumber.length === 0 || 
        filters.equipmentNumber.includes(String(equipment.equipmentNumber || equipment.equipment_number || ''));

      // Equipment name filter
      const matchesEquipmentName = filters.equipmentName.length === 0 || 
        filters.equipmentName.includes(String(equipment.equipmentName || equipment.equipment_name || ''));

      // Equipment type filter
      const matchesEquipmentType = filters.equipmentType.length === 0 || 
        filters.equipmentType.includes(String(equipment.equipmentName || equipment.equipment_name || ''));

      // Serial number filter
      const matchesSerialNumber = filters.serialNumber.length === 0 || 
        filters.serialNumber.includes(String(equipment.serial_number || ''));

      return matchesSearch && matchesEquipmentNumber && matchesEquipmentName && matchesEquipmentType && matchesSerialNumber;
    });
  }, [equipmentList, searchQuery, filters]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return searchQuery !== '' || 
           filters.equipmentNumber.length > 0 || 
           filters.equipmentName.length > 0 ||
           filters.equipmentType.length > 0 ||
           filters.serialNumber.length > 0;
  }, [searchQuery, filters]);

  // Handle filter changes
  const onFilterChange = useCallback((key: keyof EquipmentFilters) => (value: string[]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Reset all filters
  const onResetFilters = useCallback(() => {
    setSearchQuery('');
    setFilters({
      equipmentNumber: [],
      equipmentName: [],
      equipmentType: [],
      serialNumber: [],
    });
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    filters,
    onFilterChange,
    uniqueEquipmentNumbers,
    uniqueEquipmentNames,
    uniqueEquipmentTypes,
    uniqueSerialNumbers,
    filteredEquipment,
    hasActiveFilters,
    filteredCount: filteredEquipment.length,
    onResetFilters,
  };
};

